import {
  computeGameweekDetail,
  computeGameweekResult,
  computeSeasonStats,
  computeFormHistory,
} from '../season'
import { computePLTable } from '../plStandings'
import {
  computeGameweekPointsSoFar,
  computeRevealedResults,
} from '../components/gameweekGrid/gameweekPoints'
import type { SimGameStatus } from '../gameweekSimulation'
import { getGamesByGameweek, getActualResultsByGameweek } from '../seasonData'
import { mockPLTable } from '../mockProfile'
import type { Game, GameweekResult, PLTableEntry, Prediction } from '../types'

type Results = Record<string, { homeScore: number; awayScore: number }>

export interface UseSeasonScoresOptions {
  currentGameweek: number
  games: Game[]
  predictions: Record<string, Prediction>
  actualResults: Results
  bankerGameId: string | null
  /** The gameweek-level flag: every result is in and the gameweek is over. */
  resultsRevealed: boolean
  /** Whether a fixture that has a result may show it — see `src/reveal.ts`. */
  resultsVisible: boolean
  isDoubleUpActive: boolean
  isCleanSweepActive: boolean
  isGoalfestActive: boolean
  seasonHistory: GameweekResult[]
  /** Per-fixture playback status while the dev-tools gameweek simulation runs. Only
   *  `liveGameweekPoints` reads it — the season totals stay on the wholesale reveal. */
  simGameStatuses?: Record<string, SimGameStatus>
}

export interface SeasonScores {
  /**
   * True once this gameweek is **over** — every result in, and a scored row archived for it.
   *
   * Both halves matter since scoring became continuous: a row now exists from the
   * moment the first fixture of the gameweek finishes, so "has a row" no longer means "is history".
   * The distinction is what keeps the other-player "GW N Calls" window open through the afternoon
   * instead of slamming shut on the first full-time whistle.
   */
  isCurrentGwFinalized: boolean
  /** The current gameweek's results, or `{}` while they are still under wraps. */
  revealedResults: Results
  plTable: PLTableEntry[]
  overallScore: number
  currentGameweekPoints: number
  /** Current-gameweek points as the gameweek grid's GW-points display computes them:
   *  `computeRevealedResults` + `computeGameweekPointsSoFar`, gated on the same
   *  `isGameRevealed` (wholesale reveal, or a simulated fixture at full time). Unlike
   *  `currentGameweekPoints` it therefore ticks up fixture by fixture during a live
   *  simulation, which is what the profile's total-points delta shows.
   *  One derivation, so the two displays can never disagree. */
  liveGameweekPoints: number
  seasonStats: ReturnType<typeof computeSeasonStats>
  formHistory: ReturnType<typeof computeFormHistory>
  /** Weekday of the season's first kickoff, or null once results start flowing. */
  seasonStartLabel: string | null
}

/**
 * Every number the profile, the leagues landing and the PL table read,
 * derived from the same three inputs (fixtures, picks, results) rather than stored.
 *
 * Point totals are inert by default: they advance only as a result of an explicit
 * dev-tool action, never from the ambient clock. The old time-gated auto-reveal — which let the
 * live total climb day by day on its own — is deliberately gone, so a fresh account reads 0
 * across every counter until results are revealed or a gameweek is finalized.
 */
export function useSeasonScores({
  currentGameweek,
  games,
  predictions,
  actualResults,
  bankerGameId,
  resultsRevealed,
  resultsVisible,
  isDoubleUpActive,
  isCleanSweepActive,
  isGoalfestActive,
  seasonHistory,
  simGameStatuses,
}: UseSeasonScoresOptions): SeasonScores {
  // The server's row for this gameweek, which appears as soon as the first
  // fixture finishes and is rewritten after every one after it. Every *arithmetic* use below keys
  // off its presence — it is the authoritative total, and adding a locally derived one on top of
  // it would double-count the same points.
  const scoredCurrentGw = seasonHistory.find((gw) => gw.gameweek === currentGameweek)
  const isCurrentGwFinalized = scoredCurrentGw !== undefined && resultsRevealed
  const revealedResults = resultsVisible ? actualResults : {}

  // Exactly GameweekGrid's own `isGameRevealed` — a fixture counts once its result is visible
  // (per-fixture against real football, wholesale in mock), or once the simulation has played it to
  // full time. In-play scores (`simLiveResults`) deliberately don't count: points land when a
  // fixture settles.
  const isGameRevealed = (gameId: string) =>
    resultsVisible || simGameStatuses?.[gameId] === 'final'
  // Reads the authoritative archived total once scored, for the same reason
  // `currentGameweekPoints` does — a dev "finalize" can land ahead of the playback.
  const liveGameweekPoints = scoredCurrentGw
    ? scoredCurrentGw.points
    : computeGameweekPointsSoFar({
        games,
        predictions,
        revealedResults: computeRevealedResults(games, actualResults, isGameRevealed),
        bankerGameId,
        isDoubleUpActive,
        isGoalfestActive,
        isCleanSweepActive,
      })

  // PL standings reflect every gameweek before the current one in full (they are already behind
  // us in the simulated season) plus whatever of the current gameweek's results have been
  // revealed so far, mirroring how `revealedResults` gates the live total.
  const simulatedPLTable = computePLTable(
    getGamesByGameweek(),
    { ...getActualResultsByGameweek(), [currentGameweek]: revealedResults },
    currentGameweek
  )

  const liveCurrentGwPoints = computeGameweekDetail(
    currentGameweek,
    games,
    predictions,
    revealedResults,
    bankerGameId,
    isDoubleUpActive,
    isCleanSweepActive,
    isGoalfestActive
  ).totalPoints

  // Once a scored row exists the authoritative total lives in `seasonHistory` — read that rather
  // than `liveCurrentGwPoints`, which could still be catching up right after a dev "finalize" jumps
  // ahead of real kickoff time. Before that it is exactly "points so far".
  //
  // Keyed on the row's *presence*, not on the gameweek being over: against real football the server
  // writes a partial row all afternoon, and appending a locally derived row beside it would count
  // the same fixtures twice in every season stat below.
  const liveCurrentGwResult =
    resultsVisible && scoredCurrentGw === undefined && Object.keys(revealedResults).length > 0
      ? computeGameweekResult(
          computeGameweekDetail(
            currentGameweek,
            games,
            predictions,
            revealedResults,
            bankerGameId,
            isDoubleUpActive,
            isCleanSweepActive,
            isGoalfestActive
          ),
          bankerGameId
        )
      : null

  const seasonStatsHistory = liveCurrentGwResult
    ? [...seasonHistory, liveCurrentGwResult]
    : seasonHistory

  // Season-opener signal for the Leagues landing page: before any results have
  // landed, every league row shows `Season starts {day}` with an en-dash rank instead of a
  // standing.
  const seasonStartLabel =
    seasonHistory.length === 0 && !resultsRevealed
      ? (() => {
          const kickoffs = games
            .map((g) => new Date(g.kickoff).getTime())
            .filter((t) => !Number.isNaN(t))
          if (kickoffs.length === 0) return null
          return new Date(Math.min(...kickoffs)).toLocaleDateString(undefined, {
            weekday: 'short',
          })
        })()
      : null

  return {
    isCurrentGwFinalized,
    revealedResults,
    plTable: simulatedPLTable.some((entry) => entry.played > 0) ? simulatedPLTable : mockPLTable,
    overallScore:
      seasonHistory.reduce((sum, gw) => sum + gw.points, 0) +
      (scoredCurrentGw ? 0 : liveCurrentGwPoints),
    currentGameweekPoints: scoredCurrentGw ? scoredCurrentGw.points : liveCurrentGwPoints,
    liveGameweekPoints,
    seasonStats: computeSeasonStats(seasonStatsHistory),
    formHistory: computeFormHistory(seasonStatsHistory),
    seasonStartLabel,
  }
}
